import { createContext, useContext, useMemo, useState } from "react"

/**
 * @typedef {Object} ContextBajaType
 * @property {boolean} reload - Indica si se debe recargar la información.
 * @property {() => void} refresh - Función que alterna el estado de `reload` para forzar una recarga.
 * @property {any} baja - Valor actual de la baja (puede ser un objeto o `null`).
 * @property {(value: any) => void} setBaja - Función para actualizar el valor de `baja`.
 */

/**
 * Contexto global para manejar el estado de bajas.
 *
 * @type {import("react").Context<ContextBajaType | undefined>}
 */
export const ContextBaja = createContext()

/**
 * Proveedor del contexto de bajas.
 *
 * @param {Object} props - Propiedades del proveedor.
 * @param {boolean} props.reload - Estado inicial que controla si se debe recargar la información.
 * @param {import("react").Dispatch<import("react").SetStateAction<boolean>>} props.setReload - Setter del estado `reload`.
 * @param {React.ReactNode} props.children - Componentes hijos que tendrán acceso al contexto.
 *
 * @returns {JSX.Element} Proveedor del contexto con los valores compartidos.
 */
export function ContextBajaProvider({ reload, setReload, children }) {
	const [baja, setBaja] = useState(null)

	const datos = useMemo(
		() => ({
			reload,
			refresh: () => setReload((old) => !old),
			baja,
			setBaja,
		}),
		[baja, reload, setReload]
	)

	return <ContextBaja.Provider value={datos}>{children}</ContextBaja.Provider>
}

/**
 * Hook para consumir el contexto de bajas.
 *
 * @returns {ContextBajaType} Valores y funciones expuestas por el contexto.
 */
export function useContextBaja() {
	return useContext(ContextBaja)
}
